import { Stack, Typography } from '@mui/material';
import LeadFolders from 'features/lead/lead-management/lead-manage-questionnaires/folder-item';
import QuestionnaireList from 'features/lead/lead-management/lead-manage-questionnaires/questionnaire-list';
import PaiLink from 'features/ui/base-components/pai-link';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';
import {
  addLeadFolder,
  addLeadSubFolder,
  createClosedLeadQuestion,
  createEffectLeadQuestions,
  createOpenLeadQuestions,
  deleteLeadTag,
  editLeadTag,
  getAllLeadFolders,
  getAllLeadTagsByFolderId,
  moveLeadTag,
} from '../API/lead';
import EditQuestionnaireDialog from '../features/lead/lead-management/lead-manage-questionnaires/edit-questionnaire-dialog';
import MoveQuestionnaireDialog from '../features/lead/lead-management/lead-manage-questionnaires/move-questionnaire-dialog';
import AddNewClosedQuestionDialog from '../features/lead/lead-management/manage-questions-dialogs/add-new-closed-question-dialog';
import AddNewEffectQuestionsDialog from '../features/lead/lead-management/manage-questions-dialogs/add-new-effec-question-dialog';
import AddNewOpenQuestionsDialog from '../features/lead/lead-management/manage-questions-dialogs/add-new-open-question-dialog';

import AddFolderDialog from 'features/lead/lead-management/lead-manage-questionnaires/add-folder-dialog';
import AddSubFolderDialog from 'features/lead/lead-management/lead-manage-questionnaires/add-subfolder-dialog';
import ConfirmDeleteQuestionnaireDialog from 'features/lead/lead-management/lead-manage-questionnaires/confirm-delete-questionnaire-dialog';
import useErrorStore from 'stores/error-store';
import { Routes } from '../enums';
import {
  AddLeadTagFolderDialogState,
  AddLeadTagSubFolderDialogState,
  ConfirmDeleteQuestionnaireDialogState,
  EditQuestionnaireDialogState,
  Folder,
  MainFolder,
  MoveQuestionnaireDialogState,
  Questionnaire,
} from '../features/lead/lead-management/lead-manage-questionnaires/lead-manage-questionnaires.types';
import {
  AddNewClosedQuestionDialogState,
  AddNewEffectQuestionsDialogState,
  AddNewOpenQuestionsDialogState,
  CreateClosedQuestionPayload,
  CreateEffectQuestionPayload,
  QuestionnaireLanguages,
  i18nQuestion,
} from '../features/lead/lead-management/lead-questionnaire-details/lead-questionnaire-details.types';
import Logger from '../logger';

const LeadManageQuestionnaires: React.FC = () => {
  const { classes, cx } = useStyles();
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const navigate = useNavigate();
  let typingTimer: ReturnType<typeof setTimeout>;

  const [leadTagFolders, setLeadTagFolders] = useState<MainFolder[]>([]);
  const [allFolders, setAllFolders] = useState<Folder[]>([]);
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string>('');

  const [confirmDeleteQuestionnaireDialog, setConfirmDeleteQuestionnaireDialog] = useState<ConfirmDeleteQuestionnaireDialogState>({
    open: false,
    tag: {
      id: '',
      completed: false,
      name: '',
      participants: [],
      questionCount: 0,
      updatedAt: '',
    },
  });
  const [editQuestionnaireDialog, setEditQuestionnaireDialog] = useState<EditQuestionnaireDialogState>({ open: false, name: '' });
  const [moveQuestionnaireDialog, setMoveQuestionnaireDialog] = useState<MoveQuestionnaireDialogState>({ open: false, selectedFolder: '' });
  const [addLeadTagFolderDialog, setAddLeadTagFolderDialog] = useState<AddLeadTagFolderDialogState>({ open: false, name: '' });
  const [addLeadTagSubFolderDialog, setAddLeadTagSubFolderDialog] = useState<AddLeadTagSubFolderDialogState>({ open: false, name: '', folderId: '' });

  const [questionnairesLoading, setQuestionnairesLoading] = useState(true);
  const [foldersLoading, setFoldersLoading] = useState(true);
  const sortBy = 'updatedAt:desc';
  const [searchFieldQuestionnaires, setSearchFieldQuestionnaires] = useState('');
  const [searchFieldFolders, setSearchFieldFolders] = useState('');

  const [addNewClosedQuestionDialog, setAddNewClosedQuestionDialog] = useState<AddNewClosedQuestionDialogState>({
    open: false,
    categoryName: { de: '', en: '' },
    categoryInstructionSelf: { de: '', en: '' },
    categoryInstructionExternal: { de: '', en: '' },
    subCategories: [
      {
        name: { de: '', en: '' },
        description: { de: '', en: '' },
        questions: [{ selfAssessment: { de: '', en: '' }, externalAssessment: { de: '', en: '' } }],
      },
    ],
  });
  const [addNewEffectQuestionsDialog, setAddNewEffectQuestionsDialog] = useState<AddNewEffectQuestionsDialogState>({
    open: false,
    subCategories: [
      {
        name: { de: '', en: '' },
        questions: [{ selfAssessment: { de: '', en: '' }, externalAssessment: { de: '', en: '' } }],
      },
    ],
  });
  const [addNewOpenQuestionsDialog, setAddNewOpenQuestionsDialog] = useState<AddNewOpenQuestionsDialogState>({
    open: false,
    questions: [{ selfAssessment: { de: '', en: '' }, externalAssessment: { de: '', en: '' } }],
  });

  const compare = (sort: string) => {
    return (a: any, b: any) => {
      const parts = sort.split(':');
      const param = parts[0];
      const direction = parts[1];
      if (a[param] < b[param]) {
        return direction === 'asc' ? -1 : 1;
      }
      if (a[param] > b[param]) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    };
  };

  const fetchTags = async (folderId: string = selectedFolder, subFolderIndex?: number) => {
    setQuestionnairesLoading(true);
    const res = await getAllLeadTagsByFolderId(folderId);
    if (res && res.data && !res.error) {
      if (subFolderIndex !== undefined) {
        setQuestionnaires(res.data.subFolders[subFolderIndex].content.leadTags.sort(compare(sortBy)));
      } else {
        setQuestionnaires(res.data.leadTags.sort(compare(sortBy)));
      }
    } else if (res.error) {
      setQuestionnaires([]);
      handleOpenErrorDialog(res?.error, '/lead/tag-folders/:folderId', 'get');
    }
    setQuestionnairesLoading(false);
  };

  const fetchFolders = async () => {
    setFoldersLoading(true);
    const res = await getAllLeadFolders();
    if (res && !res.error) {
      const resFolders: MainFolder[] = res;
      setLeadTagFolders(resFolders);
      if (resFolders[0] !== null && resFolders[0] !== undefined && selectedFolder === '') {
        setSelectedFolder(resFolders[0].id);
        fetchTags(resFolders[0].id);
      } else {
        setQuestionnairesLoading(false);
      }
    } else if (res.error) {
      setLeadTagFolders([]);
      handleOpenErrorDialog(res?.error, '/lead/tag-folders', 'get');
    }
    setFoldersLoading(false);
  };

  useEffect(() => {
    fetchFolders();
  }, []);

  useEffect(() => {
    const folders: Folder[] = [];
    leadTagFolders.forEach((mainFolder) => {
      folders.push({ name: mainFolder.name, id: mainFolder.id, recordOwner: mainFolder.recordOwner });
      mainFolder.subFolders.forEach((subFolder) => folders.push({ name: subFolder.name, id: subFolder.id, recordOwner: subFolder.recordOwner }));
    });
    setAllFolders(folders);
  }, [leadTagFolders]);

  /**
   * Handlers for search
   */

  const handleQuestionnaireSearch = () => {
    if (searchFieldQuestionnaires.trim() === '') {
      fetchTags();
    } else {
      const updateQuestionnaires = [...questionnaires];
      const searchResults = updateQuestionnaires.filter((item) =>
        item.name.trim().toLowerCase().includes(searchFieldQuestionnaires.trim().toLowerCase())
      );
      setQuestionnaires(searchResults);
    }
  };

  const handleFolderSearch = () => {
    if (searchFieldFolders.trim() === '') {
      fetchFolders();
    } else {
      const updateFolders = [...leadTagFolders];
      const searchResults = updateFolders.filter((item) => item.name.trim().toLowerCase().includes(searchFieldFolders.trim().toLowerCase()));
      setLeadTagFolders(searchResults);
    }
  };

  const handleQuestionnaireSearchFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchFieldQuestionnaires(e.target.value);
  };

  const handleFolderSearchFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchFieldFolders(e.target.value);
  };

  const questionnaireSearchKeyUp = () => {
    clearTimeout(typingTimer);
    typingTimer = setTimeout(() => {
      handleQuestionnaireSearch();
    }, 300);
  };

  const folderSearchKeyUp = () => {
    clearTimeout(typingTimer);
    typingTimer = setTimeout(() => {
      handleFolderSearch();
    }, 300);
  };

  const searchKeyDown = () => {
    clearTimeout(typingTimer);
  };

  /**
   * Create questionnaire handlers
   */

  const createQuestionnaire = (leadTagFolderId: string) => {
    return () => {
      navigate(`${Routes.LEAD_CREATION_DETAILS}?leadTagFolderId=${leadTagFolderId}`);
    };
  };

  /**
   * Handlers for questionnaire deletion
   */

  const handleOpenConfirmDeleteQuestionnaireDialog = (tag: Questionnaire) => {
    return () => {
      setConfirmDeleteQuestionnaireDialog({ open: true, tag });
    };
  };

  const handleCloseConfirmDeleteQuestionnaireDialog = () => {
    setConfirmDeleteQuestionnaireDialog({
      open: false,
      tag: {
        id: '',
        completed: false,
        name: '',
        participants: [],
        questionCount: 0,
        updatedAt: '',
      },
    });
  };

  const handleDeleteQuestionnaire = (id: string) => {
    return async () => {
      const res = await deleteLeadTag(id);
      if (!res.error) {
        fetchTags();
        handleCloseConfirmDeleteQuestionnaireDialog();
      } else {
        handleOpenErrorDialog(res?.error, '/lead/tags/:leadTagId', 'delete');
      }
    };
  };

  /**
   * Handlers for editing a questionnaire
   */

  const handleOpenEditQuestionnaireDialog = (questionnaire: Questionnaire) => {
    return () => {
      setEditQuestionnaireDialog({ open: true, name: questionnaire.name, questionnaire });
    };
  };

  const handleCloseEditQuestionnaireDialog = () => {
    setEditQuestionnaireDialog({ open: false, name: '', questionnaire: undefined });
  };

  const handleEditQuestionnaireTextfieldChange = (type: 'name') => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateQuestionnaireDialog = { ...editQuestionnaireDialog };
      updateQuestionnaireDialog[type] = event.target.value;
      setEditQuestionnaireDialog(updateQuestionnaireDialog);
    };
  };

  const handleEditQuestionnaire = (id: string) => {
    return async () => {
      const res = await editLeadTag(id, editQuestionnaireDialog.name);
      if (!res.error) {
        fetchTags();
        handleCloseEditQuestionnaireDialog();
      } else {
        handleOpenErrorDialog(res?.error, '/lead/tags/:leadTagId', 'put');
      }
    };
  };

  /**
   * Handlers for moving a questionnaire
   */

  const handleOpenMoveQuestionnaireDialog = (questionnaire: Questionnaire) => {
    return () => {
      setMoveQuestionnaireDialog({ open: true, selectedFolder: '', questionnaire });
    };
  };

  const handleCloseMoveQuestionnaireDialog = () => {
    setMoveQuestionnaireDialog({ open: false, selectedFolder: '', questionnaire: undefined });
  };

  const handleSelectedFolderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMoveQuestionnaireDialog({ ...moveQuestionnaireDialog, selectedFolder: event.target.value });
  };

  const handleMoveQuestionnaire = (id: string) => {
    return async () => {
      const res = await moveLeadTag(id, moveQuestionnaireDialog.selectedFolder);
      if (!res.error) {
        fetchTags(moveQuestionnaireDialog.selectedFolder);
        setSelectedFolder(moveQuestionnaireDialog.selectedFolder);
        handleCloseMoveQuestionnaireDialog();
      } else {
        handleOpenErrorDialog(res?.error, '/lead/tags/:leadTagId', 'put');
      }
    };
  };

  /**
   * Handlers for lead tag folders
   */

  const handleOpenAddLeadTagFolderDialog = () => {
    setAddLeadTagFolderDialog({ ...addLeadTagFolderDialog, open: true });
  };

  const handleCloseAddLeadTagFolderDialog = () => {
    setAddLeadTagFolderDialog({ open: false, name: '' });
  };

  const handleLeadTagFolderNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddLeadTagFolderDialog({ ...addLeadTagFolderDialog, name: event.target.value });
  };

  const handleAddLeadTagFolder = async () => {
    const res = await addLeadFolder(addLeadTagFolderDialog.name);
    if (res && !res.error) {
      handleCloseAddLeadTagFolderDialog();
      fetchFolders();
    } else {
      handleOpenErrorDialog(res?.error, '/lead/tag-folders', 'post');
    }
  };

  const handleSelectFolder = (folderId: string) => {
    return () => {
      fetchTags(folderId);
      setSelectedFolder(folderId);
    };
  };

  /**
   * Handlers for lead tag sub folders
   */

  const handleOpenAddLeadTagSubFolderDialog = (folderId: string) => {
    return () => {
      setAddLeadTagSubFolderDialog({ ...addLeadTagSubFolderDialog, open: true, folderId });
    };
  };

  const handleCloseAddLeadTagSubFolderDialog = () => {
    setAddLeadTagSubFolderDialog({ open: false, name: '', folderId: '' });
  };

  const handleLeadTagSubFolderNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddLeadTagSubFolderDialog({ ...addLeadTagSubFolderDialog, name: event.target.value });
  };

  const handleAddLeadTagSubFolder = async () => {
    const res = await addLeadSubFolder(addLeadTagSubFolderDialog.folderId, addLeadTagSubFolderDialog.name);
    if (res && !res.error) {
      fetchFolders();
      handleCloseAddLeadTagSubFolderDialog();
    } else {
      handleOpenErrorDialog(res?.error, '/lead/tag-folders/:folderId', 'post');
    }
  };

  /**
   * Handlers for adding new closed questions
   */

  const handleOpenAddNewClosedQuestionDialog = () => {
    setAddNewClosedQuestionDialog({ ...addNewClosedQuestionDialog, open: true });
  };

  const handleCloseAddNewClosedQuestionDialog = () => {
    setAddNewClosedQuestionDialog({
      open: false,
      categoryName: { de: '', en: '' },
      categoryInstructionSelf: { de: '', en: '' },
      categoryInstructionExternal: { de: '', en: '' },
      subCategories: [
        {
          name: { de: '', en: '' },
          description: { de: '', en: '' },
          questions: [{ selfAssessment: { de: '', en: '' }, externalAssessment: { de: '', en: '' } }],
        },
      ],
    });
  };

  const handleCategoryNameChange = (lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setAddNewClosedQuestionDialog((prevState) => ({
        ...prevState,
        categoryName: {
          ...prevState.categoryName,
          [lang]: event.target.value,
        },
      }));
    };
  };

  const handleCategoryInstructionSelfChange = (lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setAddNewClosedQuestionDialog((prevState) => ({
        ...prevState,
        categoryInstructionSelf: {
          ...prevState.categoryInstructionSelf,
          [lang]: event.target.value,
        },
      }));
    };
  };

  const handleCategoryInstructionExternalChange = (lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setAddNewClosedQuestionDialog((prevState) => ({
        ...prevState,
        categoryInstructionExternal: {
          ...prevState.categoryInstructionExternal,
          [lang]: event.target.value,
        },
      }));
    };
  };

  const addNewSubCategory = () => {
    const updateDialog = { ...addNewClosedQuestionDialog };
    updateDialog.subCategories.push({
      name: { de: '', en: '' },
      description: { de: '', en: '' },
      questions: [{ selfAssessment: { de: '', en: '' }, externalAssessment: { de: '', en: '' } }],
    });
    setAddNewClosedQuestionDialog(updateDialog);
    Logger.trace('Added sub category to category');
  };

  const handleSubCategoryNameChange = (subCategoryIndex: number, lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateDialog = { ...addNewClosedQuestionDialog };
      updateDialog.subCategories[subCategoryIndex].name[lang] = event.target.value;
      setAddNewClosedQuestionDialog(updateDialog);
    };
  };

  const handleSubCategoryDescriptionChange = (subCategoryIndex: number, lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateDialog = { ...addNewClosedQuestionDialog };
      updateDialog.subCategories[subCategoryIndex].description[lang] = event.target.value;
      setAddNewClosedQuestionDialog(updateDialog);
    };
  };

  const removeSubCategory = (subCategoryIndex: number) => {
    return () => {
      const updateDialog = { ...addNewClosedQuestionDialog };
      updateDialog.subCategories.splice(subCategoryIndex, 1);
      setAddNewClosedQuestionDialog(updateDialog);
    };
  };

  const addNewQuestionToClosedQuestionSubCategory = (subCategoryIndex: number) => {
    return () => {
      const updateDialog = { ...addNewClosedQuestionDialog };
      updateDialog.subCategories[subCategoryIndex].questions.push({ selfAssessment: { de: '', en: '' }, externalAssessment: { de: '', en: '' } });
      setAddNewClosedQuestionDialog(updateDialog);
    };
  };

  const handleSelfAssessmentQuestionChange = (subCategoryIndex: number, questionIndex: number, lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateDialog = { ...addNewClosedQuestionDialog };
      updateDialog.subCategories[subCategoryIndex].questions[questionIndex].selfAssessment[lang] = event.target.value;
      setAddNewClosedQuestionDialog(updateDialog);
    };
  };

  const handleExternalAssessmentQuestionChange = (subCategoryIndex: number, questionIndex: number, lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateDialog = { ...addNewClosedQuestionDialog };
      updateDialog.subCategories[subCategoryIndex].questions[questionIndex].externalAssessment[lang] = event.target.value;
      setAddNewClosedQuestionDialog(updateDialog);
    };
  };

  const removeQuestionFromClosedQuestionSubCategory = (subCategoryIndex: number, questionIndex: number) => {
    return () => {
      const updateDialog = { ...addNewClosedQuestionDialog };
      updateDialog.subCategories[subCategoryIndex].questions.splice(questionIndex, 1);
      setAddNewClosedQuestionDialog(updateDialog);
    };
  };

  const handleAddNewClosedQuestions = async () => {
    const payload: CreateClosedQuestionPayload[] = [
      {
        name: addNewClosedQuestionDialog.categoryName,
        selfIntroduction: addNewClosedQuestionDialog.categoryInstructionSelf,
        externalIntroduction: addNewClosedQuestionDialog.categoryInstructionExternal,
        subCategories: addNewClosedQuestionDialog.subCategories,
      },
    ];
    const res = await createClosedLeadQuestion(payload);
    if (res && !res.error) {
      handleCloseAddNewClosedQuestionDialog();
    } else {
      handleOpenErrorDialog(res?.error, '/lead/closed-questions', 'post');
    }
  };

  /**
   * Handlers for adding new effect questions
   */

  const handleOpenAddNewEffectQuestionsDialog = () => {
    setAddNewEffectQuestionsDialog({ ...addNewEffectQuestionsDialog, open: true });
  };

  const handleCloseAddNewEffectQuestionsDialog = () => {
    setAddNewEffectQuestionsDialog({
      open: false,
      subCategories: [
        {
          name: { de: '', en: '' },
          questions: [{ selfAssessment: { de: '', en: '' }, externalAssessment: { de: '', en: '' } }],
        },
      ],
    });
  };

  const handleEffectSubCategoryNameChange = (subCategoryIndex: number, lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateDialog = { ...addNewEffectQuestionsDialog };
      updateDialog.subCategories[subCategoryIndex].name[lang] = event.target.value;
      setAddNewEffectQuestionsDialog(updateDialog);
    };
  };

  const handleEffectSelfAssessmentQuestionChange = (subCategoryIndex: number, questionIndex: number, lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateDialog = { ...addNewEffectQuestionsDialog };
      updateDialog.subCategories[subCategoryIndex].questions[questionIndex].selfAssessment[lang] = event.target.value;
      setAddNewEffectQuestionsDialog(updateDialog);
    };
  };

  const handleEffectExternalAssessmentQuestionChange = (subCategoryIndex: number, questionIndex: number, lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateDialog = { ...addNewEffectQuestionsDialog };
      updateDialog.subCategories[subCategoryIndex].questions[questionIndex].externalAssessment[lang] = event.target.value;
      setAddNewEffectQuestionsDialog(updateDialog);
    };
  };

  const removeQuestionFromEffectQuestionSubCategory = (subCategoryIndex: number, questionIndex: number) => {
    return () => {
      const updateDialog = { ...addNewEffectQuestionsDialog };
      updateDialog.subCategories[subCategoryIndex].questions.splice(questionIndex, 1);
      setAddNewEffectQuestionsDialog(updateDialog);
    };
  };

  const removeEffectSubCategory = (subCategoryIndex: number) => {
    return () => {
      const updateDialog = { ...addNewEffectQuestionsDialog };
      updateDialog.subCategories.splice(subCategoryIndex, 1);
      setAddNewEffectQuestionsDialog(updateDialog);
    };
  };

  const addNewQuestionToEffectQuestionSubCategory = (subCategoryIndex: number) => {
    return () => {
      const updateDialog = { ...addNewEffectQuestionsDialog };
      updateDialog.subCategories[subCategoryIndex].questions.push({ selfAssessment: { de: '', en: '' }, externalAssessment: { de: '', en: '' } });
      setAddNewEffectQuestionsDialog(updateDialog);
    };
  };

  const addNewEffectSubCategory = () => {
    const updateDialog = { ...addNewEffectQuestionsDialog };
    updateDialog.subCategories.push({
      name: { de: '', en: '' },
      questions: [{ selfAssessment: { de: '', en: '' }, externalAssessment: { de: '', en: '' } }],
    });
    setAddNewEffectQuestionsDialog(updateDialog);
  };

  const handleAddNewEffectQuestions = async () => {
    const payload: CreateEffectQuestionPayload[] = addNewEffectQuestionsDialog.subCategories.map((subCategory) => ({
      name: { de: subCategory.name.de, en: subCategory.name.en },
      questions: subCategory.questions,
    }));
    const res = await createEffectLeadQuestions(payload);
    if (res && !res.error) {
      handleCloseAddNewEffectQuestionsDialog();
    } else {
      handleOpenErrorDialog(res?.error, '/lead/effect-questions', 'post');
    }
  };

  /**
   * Handlers for adding new open questions
   */

  const handleOpenAddNewOpenQuestionsDialog = () => {
    setAddNewOpenQuestionsDialog({ ...addNewOpenQuestionsDialog, open: true });
  };

  const handleCloseAddNewOpenQuestionsDialog = () => {
    setAddNewOpenQuestionsDialog({ open: false, questions: [{ selfAssessment: { de: '', en: '' }, externalAssessment: { de: '', en: '' } }] });
  };

  const addNewOpenQuestion = () => {
    const updateDialog = { ...addNewOpenQuestionsDialog };
    updateDialog.questions.push({ selfAssessment: { de: '', en: '' }, externalAssessment: { de: '', en: '' } });
    setAddNewOpenQuestionsDialog(updateDialog);
  };

  const removeOpenQuestion = (questionIndex: number) => {
    return () => {
      const updateDialog = { ...addNewOpenQuestionsDialog };
      updateDialog.questions.splice(questionIndex, 1);
      setAddNewOpenQuestionsDialog(updateDialog);
    };
  };

  const handleOpenSelfAssessmentQuestionChange = (questionIndex: number, lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateDialog = { ...addNewOpenQuestionsDialog };
      updateDialog.questions[questionIndex].selfAssessment[lang] = event.target.value;
      setAddNewOpenQuestionsDialog(updateDialog);
    };
  };

  const handleOpenExternalAssessmentQuestionChange = (questionIndex: number, lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateDialog = { ...addNewOpenQuestionsDialog };
      updateDialog.questions[questionIndex].externalAssessment[lang] = event.target.value;
      setAddNewOpenQuestionsDialog(updateDialog);
    };
  };

  const handleAddNewOpenQuestions = async () => {
    const payload: i18nQuestion[] = addNewOpenQuestionsDialog.questions;
    const res = await createOpenLeadQuestions(payload);
    if (res && !res.error) {
      handleCloseAddNewOpenQuestionsDialog();
    } else {
      handleOpenErrorDialog(res?.error, '/lead/open-questions', 'post');
    }
  };

  return (
    <>
      <Stack className={classes.topActions} direction={'row'} alignItems={'center'} gap={2}>
        <PaiLink text={'+ Kategorie'} variant={'primary'} underline={'always'} size={'md'} action={handleOpenAddNewClosedQuestionDialog} />
        <PaiLink text={'+ Effektive Führung'} variant={'primary'} underline={'always'} size={'md'} action={handleOpenAddNewEffectQuestionsDialog} />
        <PaiLink text={'+ Persönliche Eindrücke'} variant={'primary'} underline={'always'} size={'md'} action={handleOpenAddNewOpenQuestionsDialog} />
      </Stack>
      <Stack className={classes.leadCreation} direction={{ xs: 'column', xl: 'row' }} gap={1}>
        <div className={cx(classes.content, 'small')}>
          <div className={classes.title}>
            <Typography className={classes.titleText}>LEaD Ordner</Typography>
          </div>
          <LeadFolders
            loadingFolders={foldersLoading}
            leadTagFolders={leadTagFolders}
            selectedFolder={selectedFolder}
            searchFieldFolders={searchFieldFolders}
            handleFolderSearchFieldChange={handleFolderSearchFieldChange}
            searchKeyDown={searchKeyDown}
            folderSearchKeyUp={folderSearchKeyUp}
            handleSelectFolder={handleSelectFolder}
            handleOpenAddLeadTagFolderDialog={handleOpenAddLeadTagFolderDialog}
            handleOpenAddLeadTagSubFolderDialog={handleOpenAddLeadTagSubFolderDialog}
            createQuestionnaire={createQuestionnaire}
          />
        </div>
        <div className={cx(classes.content, 'large')}>
          <div className={classes.title}>
            <Typography className={classes.titleText}>LEaD Umfragen</Typography>
          </div>
          <QuestionnaireList
            questionnairesLoading={questionnairesLoading}
            questionnaires={questionnaires}
            searchFieldQuestionnaires={searchFieldQuestionnaires}
            handleQuestionnaireSearchFieldChange={handleQuestionnaireSearchFieldChange}
            questionnaireSearchKeyUp={questionnaireSearchKeyUp}
            searchKeyDown={searchKeyDown}
            handleOpenConfirmDeleteQuestionnaireDialog={handleOpenConfirmDeleteQuestionnaireDialog}
            handleOpenEditQuestionnaireDialog={handleOpenEditQuestionnaireDialog}
            handleOpenMoveQuestionnaireDialog={handleOpenMoveQuestionnaireDialog}
          />
        </div>
      </Stack>

      <AddFolderDialog
        open={addLeadTagFolderDialog.open}
        newFolderName={addLeadTagFolderDialog.name}
        handleLeadTagFolderNameChange={handleLeadTagFolderNameChange}
        handleCloseAddLeadTagFolderDialog={handleCloseAddLeadTagFolderDialog}
        handleAddLeadTagFolder={handleAddLeadTagFolder}
      />
      <AddSubFolderDialog
        open={addLeadTagSubFolderDialog.open}
        newSubFolderName={addLeadTagSubFolderDialog.name}
        handleLeadTagSubFolderNameChange={handleLeadTagSubFolderNameChange}
        handleCloseAddLeadTagSubFolderDialog={handleCloseAddLeadTagSubFolderDialog}
        handleAddLeadTagSubFolder={handleAddLeadTagSubFolder}
      />

      {editQuestionnaireDialog.questionnaire && (
        <EditQuestionnaireDialog
          open={editQuestionnaireDialog.open}
          name={editQuestionnaireDialog.name}
          handleTextFieldChange={handleEditQuestionnaireTextfieldChange}
          handleClose={handleCloseEditQuestionnaireDialog}
          handleProceed={handleEditQuestionnaire(editQuestionnaireDialog.questionnaire.id)}
        />
      )}
      {moveQuestionnaireDialog.questionnaire && (
        <MoveQuestionnaireDialog
          open={moveQuestionnaireDialog.open}
          handleClose={handleCloseMoveQuestionnaireDialog}
          handleProceed={handleMoveQuestionnaire(moveQuestionnaireDialog.questionnaire.id)}
          allFolders={allFolders.filter((folder) => folder.id !== selectedFolder)}
          selectedFolder={moveQuestionnaireDialog.selectedFolder}
          handleSelectedFolderChange={handleSelectedFolderChange}
        />
      )}

      <AddNewOpenQuestionsDialog
        open={addNewOpenQuestionsDialog.open}
        questions={addNewOpenQuestionsDialog.questions}
        handleClose={handleCloseAddNewOpenQuestionsDialog}
        handleProceed={handleAddNewOpenQuestions}
        addNewOpenQuestion={addNewOpenQuestion}
        removeOpenQuestion={removeOpenQuestion}
        handleOpenSelfAssessmentQuestionChange={handleOpenSelfAssessmentQuestionChange}
        handleOpenExternalAssessmentQuestionChange={handleOpenExternalAssessmentQuestionChange}
      />
      <AddNewEffectQuestionsDialog
        open={addNewEffectQuestionsDialog.open}
        subCategories={addNewEffectQuestionsDialog.subCategories}
        handleClose={handleCloseAddNewEffectQuestionsDialog}
        handleProceed={handleAddNewEffectQuestions}
        handleEffectSubCategoryNameChange={handleEffectSubCategoryNameChange}
        handleEffectSelfAssessmentQuestionChange={handleEffectSelfAssessmentQuestionChange}
        handleEffectExternalAssessmentQuestionChange={handleEffectExternalAssessmentQuestionChange}
        removeQuestionFromEffectQuestionSubCategory={removeQuestionFromEffectQuestionSubCategory}
        removeEffectSubCategory={removeEffectSubCategory}
        addNewQuestionToEffectQuestionSubCategory={addNewQuestionToEffectQuestionSubCategory}
        addNewEffectSubCategory={addNewEffectSubCategory}
      />
      <AddNewClosedQuestionDialog
        open={addNewClosedQuestionDialog.open}
        categoryName={addNewClosedQuestionDialog.categoryName}
        categoryInstructionSelf={addNewClosedQuestionDialog.categoryInstructionSelf}
        categoryInstructionExternal={addNewClosedQuestionDialog.categoryInstructionExternal}
        subCategories={addNewClosedQuestionDialog.subCategories}
        handleClose={handleCloseAddNewClosedQuestionDialog}
        handleProceed={handleAddNewClosedQuestions}
        handleCategoryNameChange={handleCategoryNameChange}
        handleCategoryInstructionSelfChange={handleCategoryInstructionSelfChange}
        handleCategoryInstructionExternalChange={handleCategoryInstructionExternalChange}
        addNewSubCategory={addNewSubCategory}
        addNewQuestionToClosedQuestionSubCategory={addNewQuestionToClosedQuestionSubCategory}
        removeQuestionFromClosedQuestionSubCategory={removeQuestionFromClosedQuestionSubCategory}
        removeSubCategory={removeSubCategory}
        handleSubCategoryNameChange={handleSubCategoryNameChange}
        handleSubCategoryDescriptionChange={handleSubCategoryDescriptionChange}
        handleSelfAssessmentQuestionChange={handleSelfAssessmentQuestionChange}
        handleExternalAssessmentQuestionChange={handleExternalAssessmentQuestionChange}
      />

      <ConfirmDeleteQuestionnaireDialog
        open={confirmDeleteQuestionnaireDialog.open}
        questionnaireName={confirmDeleteQuestionnaireDialog.tag.name}
        handleClose={handleCloseConfirmDeleteQuestionnaireDialog}
        handleConfirm={handleDeleteQuestionnaire(confirmDeleteQuestionnaireDialog.tag.id)}
      />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  topActions: {
    position: 'absolute',
    right: '32px',
    top: 0,
    height: '60px',
    zIndex: 1300,
  },
  leadCreation: {
    padding: '8px',
    background: theme.palette.backgrounds.main,
    height: 'calc(100vh - 60px - 32px)',
    maxHeight: 'calc(960px - 60px - 32px)',
    [theme.breakpoints.down('xl')]: {
      height: 'fit-content',
      maxHeight: 'unset',
    },
  },
  content: {
    background: '#FFF',
    boxShadow: Shadows.GREY_SHADOW_LIGHT,
    padding: '12px 16px',
    height: '100%',
    '&.small': {
      flexBasis: '25%',
    },
    '&.large': {
      flexBasis: '75%',
    },
    [theme.breakpoints.down('xl')]: {
      flexBasis: '100%',
    },
  },
  title: {
    marginBottom: '32px',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
    textAlign: 'center',
    textTransform: 'uppercase',
  },
}));

export default LeadManageQuestionnaires;
